import { Alert } from '../../alerts/models/Alert';
import { Timestamp } from '../../shared/common.types';
import { DeviceProperty, ResolvedSection } from '../models/DeviceDetail';
import {
    DIAGNOSTIC_PROPERTY_TYPES,
    DiagnosticSection,
    DeviceDiagnosticPropertyItemVm,
} from '../models/DeviceDiagnosticPropertyVm';
import { UiProfile } from '../models/UiProfile';

import { filterAlertsByPropertyId } from './device-alert';
import { enrichUiProperty } from './enrich-ui-property';
import { LineChartViewerVm } from './viewer-builder/builders/buildLineChartViewer';
import { ListViewerVm } from './viewer-builder/builders/buildListViewer';
import buildViewer, { SectionItemViewer } from './viewer-builder/buildViewer';

function mapViewerToDiagnosticProperty(
    viewer: SectionItemViewer,
    propertyId: string,
    label: string,
    alert: Alert | null,
    statusInformation?: string,
): DeviceDiagnosticPropertyItemVm {
    const statusInformationVm = statusInformation ? { statusInformation } : {};

    switch (viewer.type) {
        case 'list':
            return {
                type: DIAGNOSTIC_PROPERTY_TYPES.LIST,
                id: propertyId,
                label,
                values: (viewer as ListViewerVm).value,
                alert,
                ...statusInformationVm,
            };
        case 'rs100Compat':
            return {
                type: DIAGNOSTIC_PROPERTY_TYPES.RS100_COMPAT,
                id: propertyId,
                label,
                alert,
                ...statusInformationVm,
            };
        case 'lineChart': {
            const { value, config } = viewer as LineChartViewerVm;

            return {
                type: DIAGNOSTIC_PROPERTY_TYPES.LINE_CHART,
                id: propertyId,
                label,
                lineChartData: { value, config },
                alert,
                ...statusInformationVm,
            };
        }
        default:
            return {
                type: DIAGNOSTIC_PROPERTY_TYPES.TEXT,
                id: propertyId,
                label,
                value: viewer.type === 'text' ? viewer.value : '—',
                alert,
                ...statusInformationVm,
            };
    }
}

function filterReadOnlyPropertyIds(section: ResolvedSection, uiProfile: UiProfile): string[] {
    return section.propertyIds.filter((propertyId) => {
        const rawUiProperty = uiProfile.properties[propertyId];

        return rawUiProperty && !rawUiProperty.editable;
    });
}

function buildDiagnosticProperty(
    propertyId: string,
    uiProfile: UiProfile,
    properties: Record<string, DeviceProperty>,
    lastUpdateTime: Timestamp | undefined,
    deviceAlerts: Alert[],
): DeviceDiagnosticPropertyItemVm {
    const rawUiProperty = uiProfile.properties[propertyId];
    const deviceProperty = properties[propertyId];
    const enriched = enrichUiProperty(rawUiProperty, deviceProperty?.metadata);
    const viewer = buildViewer(enriched, deviceProperty, { timestamp: lastUpdateTime });
    const alert = filterAlertsByPropertyId(deviceAlerts, propertyId);

    return mapViewerToDiagnosticProperty(
        viewer,
        propertyId,
        enriched?.label ?? '',
        alert,
        rawUiProperty.status_information?.trim() || rawUiProperty.advice?.trim(),
    );
}

export function buildDiagnosticSection(
    section: ResolvedSection,
    uiProfile: UiProfile,
    properties: Record<string, DeviceProperty>,
    lastUpdateTime: Timestamp | undefined,
    deviceAlerts: Alert[],
): DiagnosticSection {
    const sectionProperties = filterReadOnlyPropertyIds(section, uiProfile).map((id) =>
        buildDiagnosticProperty(id, uiProfile, properties, lastUpdateTime, deviceAlerts),
    );

    return {
        id: section.title,
        title: section.title,
        properties: sectionProperties,
    };
}
